import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { api } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import HomeSummary from '../components/HomeSummary.js';
import { useSession } from '../auth-client.js';
import styles from './GroupsScreen.module.css';

/**
 * The signed-in home screen.
 *
 * Was purely a list of groups — a container, when what someone actually
 * arrives wanting is "what do I need to do, and what happened while I was
 * away" (PLAN.md Phase 10.27). `HomeSummary` answers those below the
 * group list: groups stay the first, familiar thing on the page, with the
 * summary as a supplement rather than something pushing navigation down.
 *
 * The summary's cards each render nothing when they have nothing to say, so
 * this screen degrades all the way back to its original shape for a new
 * account or an off-season one rather than showing empty scaffolding.
 */
const LAST_SEEN_KEY = 'bolao.home.lastSeenAt';

export default function GroupsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const { data, isPending, isError } = useQuery({
    queryKey: ['groups'],
    queryFn: api.listGroups,
  });

  /**
   * "Since your last visit" is measured from a timestamp this device owns —
   * read once into state so it survives the effect below overwriting it, or
   * the whole screen would mark everything as already-seen on first paint.
   */
  const [lastSeenAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_SEEN_KEY);
    } catch {
      return null;
    }
  });

  const locale = i18n.resolvedLanguage ?? 'en';
  const { data: summary } = useQuery({
    queryKey: ['home', locale, lastSeenAt],
    queryFn: () => api.getHome(locale, lastSeenAt),
  });

  // Stamp the visit only once the summary has actually arrived, so closing
  // the app on a spinner does not silently mark unseen results as read.
  const stamped = useRef(false);
  useEffect(() => {
    if (!summary || stamped.current) return;
    stamped.current = true;
    try {
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    } catch {
      /* Private mode or storage disabled — the summary just stops marking things new. */
    }
  }, [summary]);

  /**
   * Invitations addressed to this account's email.
   *
   * Surfaced here so an emailed invitation works even if the recipient never
   * opened a link — they sign in and the invitation is simply waiting.
   */
  const { data: pending } = useQuery({
    queryKey: ['pending-invites'],
    queryFn: api.listPendingInvites,
  });

  const accept = useMutation({
    mutationFn: (inviteId: string) =>
      api.acceptInvite(inviteId, session?.user?.name ?? t('groups.defaultName')),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      await queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
      void navigate(`/groups/${result.groupId}`);
    },
  });

  return (
    <main className="screen">
      <AppHeader />

      {pending && pending.length > 0 ? (
        <section className="stack">
          <span className="label">{t('groups.invitations')}</span>
          {pending.map((invitation) => (
            <div key={invitation.inviteId} className={`panel ${styles.card}`}>
              <div className={styles.cardMain}>
                <span className={styles.name}>{invitation.groupName}</span>
                <span className="muted">
                  {t('join.invitedBy', { name: invitation.invitedByName })}
                </span>
              </div>
              <button
                type="button"
                className="button button-primary"
                disabled={accept.isPending}
                onClick={() => accept.mutate(invitation.inviteId)}
              >
                {t('groups.acceptInvite')}
              </button>
            </div>
          ))}
        </section>
      ) : null}

      <h1>{t('groups.title')}</h1>

      {isPending ? <p className="muted">{t('common.loading')}</p> : null}
      {isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

      {data && data.length === 0 ? (
        <div className="panel" style={{ padding: 'var(--space-5)', textAlign: 'center' }}>
          <p>{t('groups.empty')}</p>
          <p className="muted" style={{ marginTop: 'var(--space-2)' }}>
            {t('groups.emptyHint')}
          </p>
        </div>
      ) : null}

      {data && data.length > 0 ? (
        <ul className={styles.list}>
          {data.map((group) => (
            <li key={group.id}>
              <Link to={`/groups/${group.id}`} className={`panel ${styles.card}`}>
                {group.imageUrl ? (
                  <img src={group.imageUrl} alt="" className={styles.cardImage} />
                ) : null}
                <div className={styles.cardMain}>
                  <span className={styles.name}>{group.name}</span>
                  <span className="muted">{t('pool.poolCount', { count: group.poolCount })}</span>
                </div>
                <div className={styles.cardMeta}>
                  {group.isOrganizer ? (
                    <span className={styles.badge}>{t('groups.organizer')}</span>
                  ) : null}
                  <span className="muted">
                    {t('groups.playerCount', { count: group.memberCount })}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      <Link to="/groups/new" className="button button-primary" style={{ textAlign: 'center' }}>
        {t('groups.create')}
      </Link>

      {summary ? <HomeSummary summary={summary} /> : null}
    </main>
  );
}
